import { useSyncExternalStore } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ─── Idiomas do app ───────────────────────────────────────────────────────────────────────────
// O texto original (português do Brasil) é a própria chave: t('Salvar') procura "Salvar" no
// dicionário do idioma escolhido e, se não achar, mostra o original. Variáveis vão entre chaves:
// t('{n} tarefa(s)', { n: 3 }). Chaves que começam com "@" são termos curtos com contexto
// (ex.: letras dos dias da semana) e têm o texto em português definido em PT abaixo.

export type Idioma = 'pt-BR' | 'pt-PT' | 'en' | 'es' | 'fr' | 'it' | 'de' | 'zh' | 'ja' | 'hi' | 'bn' | 'ru' | 'id' | 'ar';

export const IDIOMAS: { id: Idioma; nome: string; bandeira: string }[] = [
  { id: 'pt-BR', nome: 'Português (Brasil)', bandeira: '🇧🇷' },
  { id: 'pt-PT', nome: 'Português (Portugal)', bandeira: '🇵🇹' },
  { id: 'en', nome: 'English', bandeira: '🇺🇸' },
  { id: 'es', nome: 'Español', bandeira: '🇪🇸' },
  { id: 'fr', nome: 'Français', bandeira: '🇫🇷' },
  { id: 'it', nome: 'Italiano', bandeira: '🇮🇹' },
  { id: 'de', nome: 'Deutsch', bandeira: '🇩🇪' },
  { id: 'zh', nome: '中文（普通话）', bandeira: '🇨🇳' },
  { id: 'ja', nome: '日本語', bandeira: '🇯🇵' },
  { id: 'hi', nome: 'हिन्दी', bandeira: '🇮🇳' },
  { id: 'bn', nome: 'বাংলা', bandeira: '🇧🇩' },
  { id: 'ru', nome: 'Русский', bandeira: '🇷🇺' },
  { id: 'id', nome: 'Bahasa Indonesia', bandeira: '🇮🇩' },
  { id: 'ar', nome: 'العربية', bandeira: '🇸🇦' },
];

/** Termos curtos com contexto (chaves "@..."), em português. */
const PT: Record<string, string> = {
  '@letra-dom': 'D', '@letra-seg': 'S', '@letra-ter': 'T', '@letra-qua': 'Q', '@letra-qui': 'Q', '@letra-sex': 'S', '@letra-sab': 'S',
};

type Dic = Record<string, string>;
// Os arquivos ficam em /locales. Carregados todos de uma vez (texto puro, poucos KB cada).
const DIC: Partial<Record<Idioma, Dic>> = {
  'pt-PT': require('../locales/pt-PT.json'),
  en: require('../locales/en.json'),
  es: require('../locales/es.json'),
  fr: require('../locales/fr.json'),
  it: require('../locales/it.json'),
  de: require('../locales/de.json'),
  zh: require('../locales/zh.json'),
  ja: require('../locales/ja.json'),
  hi: require('../locales/hi.json'),
  bn: require('../locales/bn.json'),
  ru: require('../locales/ru.json'),
  id: require('../locales/id.json'),
  ar: require('../locales/ar.json'),
};

const valido = (x: unknown): x is Idioma => IDIOMAS.some(i => i.id === x);

/** Idioma do aparelho, quando for um dos disponíveis. */
function doAparelho(): Idioma {
  let loc = 'pt-BR';
  try { loc = Intl.DateTimeFormat().resolvedOptions().locale || loc; } catch { /* sem Intl */ }
  const l = loc.toLowerCase();
  if (l.startsWith('pt')) return l.includes('pt-pt') || l.endsWith('-pt') ? 'pt-PT' : 'pt-BR';
  const base = l.split(/[-_]/)[0] as Idioma;
  return valido(base) ? base : 'pt-BR';
}

let atual: Idioma = doAparelho();
const ouvintes = new Set<() => void>();
const CHAVE = 'mp_idioma';

export const idioma = () => atual;
export const ehRTL = () => atual === 'ar';

export function t(s: string, vars?: Record<string, string | number>): string {
  let r = (atual !== 'pt-BR' && DIC[atual]?.[s]) || PT[s] || s;
  if (vars) {
    r = plurais(r, vars);
    r = r.replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m));
  }
  return r;
}

// ─── Singular e plural (texto 13 F6) ─────────────────────────────────────────────────────────
// Os textos continuam escritos com "(s)" (ex.: "{n} dia(s) seguido(s) lendo"); na hora de mostrar, o número
// mais perto ANTES da palavra (ou, se não houver, o primeiro depois) decide: 1 → "1 dia seguido lendo",
// outro número → "2 dias seguidos lendo". Vale nos idiomas em que o plural é só acrescentar o final.
const COM_PLURAL: Partial<Record<Idioma, (n: number) => boolean>> = {
  'pt-BR': n => Math.abs(n) !== 1, 'pt-PT': n => Math.abs(n) !== 1, en: n => Math.abs(n) !== 1, es: n => Math.abs(n) !== 1,
  it: n => Math.abs(n) !== 1, de: n => Math.abs(n) !== 1, fr: n => Math.abs(n) >= 2,
};
/** Junta a palavra com o final do plural: cartão(ões) → cartões, item(ns) → itens, mês(es) → meses, entry(ies) → entries. */
function noPlural(palavra: string, fim: string) {
  const f = fim.toLowerCase();
  if (f === 'ões' && /ão$/i.test(palavra)) return palavra.slice(0, -2) + fim;
  if (f === 'ães' && /ão$/i.test(palavra)) return palavra.slice(0, -2) + fim;
  if (f === 'ns' && /m$/i.test(palavra)) return palavra.slice(0, -1) + fim;
  if (f === 'eis' && /il$/i.test(palavra)) return palavra.slice(0, -2) + fim;   // frágil(eis) → frágeis
  if (f === 'is' && /l$/i.test(palavra)) return palavra.slice(0, -1) + fim;     // anual(is) → anuais
  if (f === 'ies' && /y$/i.test(palavra)) return palavra.slice(0, -1) + fim;
  if (f === 'es' && /ês$/i.test(palavra)) return palavra.slice(0, -2) + 'eses';   // mês(es) → meses
  return palavra + fim;
}
const ehNumero = (v: unknown): number | null => {
  if (typeof v === 'number') return isFinite(v) ? v : null;
  const m = /^\s*-?\d+(?:[.,]\d+)?\s*$/.exec(String(v ?? ''));
  return m ? Number(String(v).replace(',', '.')) : null;
};
/** Texto com "(s)" sem o número dentro, ex.: a unidade depois de uma quantidade (t + singular/plural pelo número n). */
export const tn = (s: string, n: number) => plurais(t(s).replace(/\{/g, '\u0000'), { __n: n }, true).replace(/\u0000/g, '{');
function plurais(r: string, vars: Record<string, string | number>, fixo = false): string {
  const regra = COM_PLURAL[atual];
  if (!regra || !r.includes('(')) return r;
  const nums: { i: number; n: number }[] = fixo ? [{ i: -1, n: Number(vars.__n) }] : [];
  if (!fixo) r.replace(/\{(\w+)\}/g, (m, k, i: number) => { const n = ehNumero(vars[k]); if (n != null) nums.push({ i, n }); return m; });
  if (!nums.length) return r;
  return r.replace(/([\p{L}]+)\(([\p{Ll}]{1,3})\)/gu, (m, palavra: string, fim: string, i: number) => {
    const antes = nums.filter(x => x.i < i);
    const n = (antes.length ? antes[antes.length - 1] : nums[0]).n;
    return regra(n) ? noPlural(palavra, fim) : palavra;
  });
}

export function setIdioma(i: Idioma) {
  if (!valido(i) || i === atual) return;
  atual = i;
  AsyncStorage.setItem(CHAVE, i).catch(() => {});
  ouvintes.forEach(f => f());
}

/** Lê o idioma escolhido antes (fica no aparelho/navegador). Chame uma vez ao abrir o app. */
export async function carregarIdioma() {
  try {
    const s = await AsyncStorage.getItem(CHAVE);
    if (valido(s) && s !== atual) { atual = s; ouvintes.forEach(f => f()); }
  } catch { /* segue com o do aparelho */ }
}

export const useIdioma = () => useSyncExternalStore(cb => { ouvintes.add(cb); return () => ouvintes.delete(cb); }, () => atual);

/** Marca um texto para tradução sem traduzir agora (listas fixas definidas fora dos componentes).
 *  Na hora de mostrar, use t(valor). */
export const N_ = (s: string) => s;
